import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { Persister } from "@tanstack/react-query-persist-client";

/**
 * Shared React Query client.
 *
 * Defaults are tuned for the "render instantly from cache, refresh in the
 * background" behavior: queries are served from the persisted cache on boot
 * (so the first paint has data), then refetched in the background and swapped
 * in when fresh data arrives (stale-while-revalidate).
 *
 * Per-query overrides (e.g. longer staleTime for rarely-changing reference
 * lists like kapps/tags) live in the individual query hooks.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Treat data as fresh for 30s; after that a mount/focus triggers a
      // background refetch while the cached value keeps rendering.
      staleTime: 30_000,
      // Keep unused data around for a day so a reload still hydrates instantly.
      gcTime: 24 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Bumped on every edge login (saveEdgeUserLoginSession) and logout
 * (clearUserSession). The edge user is not in globals, so a component holding
 * on to a CDN token compares this to notice that the user changed.
 */
export const userSessions = { generation: 0 };

/**
 * Bump this whenever the shape of any persisted query data changes in a
 * backwards-incompatible way — it invalidates the entire on-disk cache so we
 * never hydrate stale-shaped objects into components.
 */
export const PERSIST_BUSTER = "v1";

const STORAGE_KEY = "korra-rq-cache";

export const createPersister = (): Persister | undefined => {
  if (typeof window === "undefined") return undefined;
  // Merely accessing window.localStorage can throw (privacy mode, sandboxed
  // iframes, blocked storage). Probe it in a try/catch and fall back to
  // non-persisted mode rather than crashing provider initialization.
  let storage: Storage;
  try {
    storage = window.localStorage;
    if (!storage) return undefined;
    const probe = "__korra_rq_probe__";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
  } catch {
    return undefined;
  }
  return createSyncStoragePersister({
    storage,
    key: STORAGE_KEY,
    // Coalesce rapid cache writes (e.g. optimistic favorite toggles) into at
    // most one localStorage write per second.
    throttleTime: 1000,
  });
};
